import { HttpErrorResponse } from '@angular/common/http';
import { Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GlobalComponentService } from '@techmore/global';
import { throwError } from 'rxjs';
import { isNullOrUndefined } from 'util';
import { OAuthError } from './share/entity/OauthError';
import { Router } from '@angular/router';
import { Response } from 'express';
import { LoggerService } from './share/service/logger.service';

export const isSupportObjectFit = 'objectFit' in document.documentElement.style;
// export const isSupportObjectFit = false;

export const checkIsMobile = () => {
  return /mobile|Android|iPad|iPhone/.test(window.navigator.userAgent);
};

export function loadLazyCssFile(renderer: Renderer2, parentEl: Element, styleName: string, path: string, preload?: boolean) {
  if (document.getElementById(styleName)) { return; }
  const style = renderer.createElement('link');
  const noscriptStyle = renderer.createElement('link');
  renderer.setAttribute(style, 'id', `${styleName}`);
  renderer.setAttribute(noscriptStyle, 'id', `noscript_${styleName}`);
  renderer.setAttribute(style, 'href', `${path}`);
  renderer.setAttribute(noscriptStyle, 'href', `${path}`);
  renderer.setAttribute(style, 'onload', `this.rel='stylesheet'`);
  if (preload) {
    renderer.setAttribute(style, 'rel', `preload`);
    renderer.setAttribute(noscriptStyle, 'rel', `stylesheet`);
    renderer.setAttribute(style, 'as', `style`);
  } else {
    renderer.setAttribute(style, 'rel', `stylesheet`);
  }
  const body = parentEl.closest('body');
  renderer.insertBefore(body, style, body.firstElementChild);

  if (preload) {
    const bodyNoScript = body.querySelectorAll('noscript')[2];
    if (bodyNoScript.childElementCount === 0) {
      renderer.appendChild(bodyNoScript, noscriptStyle);
    } else {
      renderer.insertBefore(bodyNoScript, noscriptStyle, bodyNoScript.firstElementChild);
    }
  }
}

export function loadLazyJsFile(renderer: Renderer2, scriptName: string, path: string, useDefer?: boolean) {
  if (document.getElementById(scriptName)) { return; }
  const script = renderer.createElement('script');
  renderer.setAttribute(script, 'id', `${scriptName}`);
  renderer.setAttribute(script, 'type', `text/javascript`);
  renderer.setAttribute(script, 'src', `${path}`);
  renderer.setAttribute(script, 'async', ``);
  if (useDefer) {
    renderer.setAttribute(script, 'defer', ``);
  }
  // 用 document 塞在 server 端部會有作用, 但沒關係這個在 client 端在執行就好
  // 用 parentEl.closest('body').previousSiblingsElement appendChild 也沒用 (但 insertBefore 沒試過)
  renderer.appendChild(document.head, script);
}

export function closestPolifill() {
  if (!Element.prototype.matches) {
    Element.prototype.matches =
      (Element.prototype as any).msMatchesSelector ||
      Element.prototype.webkitMatchesSelector;
  }

  if (!Element.prototype.closest) {
    Element.prototype.closest = function(s) {
      let el = this;

      do {
        if (typeof s === 'string' && Element.prototype.matches.call(el, s)) { return el;
        } else if (typeof s === 'object' && el === s) { return el; }
        el = el.parentElement || el.parentNode;
      } while (el !== null && el.nodeType === 1);
      return null;
    };
  }
}

/** 可餵入 HTMLElement做目標比對的 closest 方法 */
export const closest = (el, s) => {
  do {
    if (typeof s === 'string') {
      if (Element.prototype.matches.call(el, s)) { return el; }
    } else if (s instanceof HTMLElement) {
      if (el === s) { return el; }
    }
    el = el.parentElement || el.parentNode;
  } while (el !== null && el.nodeType === 1);
  return null;
};


/** 將表格的查詢參數轉換成查 API 用的查詢參數 */
export function transferTableParams2HttpListParams(queryParam: any) {
  let params: {[param: string]: string | string[];} = {};
  if (queryParam.limit) {
    params['size'] = `${queryParam.limit}`;
  }
  if (queryParam.filter) {
    params['sfilterize'] = `${queryParam.filter}`;
  }
  if (queryParam.search) {
    params['search'] = `${queryParam.search}`;
  }
  if (queryParam.limit && (queryParam.offset || queryParam.offset === 0)) {
    params['page'] = `${queryParam.offset / queryParam.limit}`;
  }
  if (queryParam.sort && queryParam.order) {
    params['sort'] = `${queryParam.sort},${queryParam.order}`;
  }
  if (queryParam.query) {
    const query = JSON.parse(queryParam.query);
    const keys = Object.keys(query);
    if (keys.length !== 0) {
      for (const k of keys) {
        if (!isNullOrUndefined(query[k])) {
          params[k] = query[k];
        }
      }
    }
  }

  return params;
}

/** 將物件中為定義的值轉成 null */
export function transferObjNullVal(customSearchEntity: any) {
  for (let keys = Object.keys(customSearchEntity), i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typeof customSearchEntity[key] === 'undefined') {
      customSearchEntity[key] = null;
    }
  }
}

/** 剖析 JWT token string 成 json 物件  */
export function parseAuthoriztionToken(token: string) {
  const base64Url = token.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  switch (base64.length % 4) {
    case 0: break;
    case 2: base64 += '=='; break;
    case 3: base64 += '='; break;
    default: break;
  }
  return decodeURIComponent(escape(atob(base64)))
}

/** 登入後操作 api 若失敗的訊息處理方式 */
export function printErrorHandler(error: any, reqUrl: string, translateService: TranslateService, globalComponentService: GlobalComponentService, words: {[key: string]: string}, isBrowser: boolean, loggerService: LoggerService) {
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 404:
        if (isBrowser) {
          globalComponentService.openErrorAlert(`${words.addressNotFound} : ${reqUrl}`, { showCopyTip: false});
        } else {
          loggerService.error(`${words.addressNotFound} : ${reqUrl}`);
        }
        break;
      case 401:
        // 不要做任何錯誤訊息打印，要在 SsoAuthService 裡處理就好
        break;
      default:
        try {
          let oauthError: OAuthError = Object.assign(new OAuthError(translateService), error.error);
          if (oauthError instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              oauthError = JSON.parse(reader.result.toString());
              if (isBrowser) {
                globalComponentService.openErrorAlert(oauthError.getMessage() + `，URL: ${reqUrl}`, { showCopyTip: true});
              } else {
                loggerService.error(oauthError.getMessage() + `，URL: ${reqUrl}`);
              }
            };
            reader.readAsText(oauthError);
          } else {
            if (isBrowser) {
              globalComponentService.openErrorAlert(oauthError.getMessage() + `，URL: ${reqUrl}`, { showCopyTip: true});
            } else {
              loggerService.error(oauthError.getMessage() + `，URL: ${reqUrl}`);
            }
          }
        } catch (e) {
          if (isBrowser) {
            globalComponentService.openErrorAlert(`${words.undefinedError}，URL: ${reqUrl}`);
          } else {
            loggerService.error(`${words.undefinedError}，URL: ${reqUrl}`, e);
          }
        }
        break;
    }
  }
  return throwError(error);
}

/** 當檔案上傳失敗事件拋出 */
export function onUploadFailedEvent(evt, globalComponentService: GlobalComponentService, isBrowser: boolean) {
  console.error(evt);

  if (isBrowser) {
    switch (evt.code) {
      case 'E1':
        globalComponentService.openErrorAlert((evt.fileName || '') + '檔案格式錯誤');
        break;
      case 'E2':
        globalComponentService.openErrorAlert((evt.fileName || '') + '檔案大小錯誤');
        break;
      case 'E3':
        globalComponentService.openErrorAlert((evt.fileName || '') + '匯入失敗: ' + (evt.msg as Error).message);
        break;
    }
  }
}

/** 打亂陣列排序 */
export function randomData(data: any[]): any[] {
  // tslint:disable-next-line: only-arrow-functions
  data.sort(function() {
    return (0.5 - Math.random());
  });
  return data;
}

export function removeUrlQueryParam(url: string): string {
  let queryIndex = url.indexOf('?');
  if (queryIndex === -1) { queryIndex = url.indexOf(';'); }
  if (queryIndex > 0) {
    url = url.substring(0, queryIndex);
  }
  return url;
}

/**
 *
 * @param url location.href
 * @returns 轉成物件的查詢參數, ex: {param1: '', param2: ''}
 */
export function parseQueryPqrams(url: string) {
  let queryParamSatrtIndex = url.indexOf('?');
  const queryParam: any = {};
  if (queryParamSatrtIndex !== -1) {
    const urlQueryParam = url.slice(queryParamSatrtIndex + 1, url.length);
    urlQueryParam.split('&').forEach(qps => {
      const a = qps.split('=');
      queryParam[a[0]] = a[1];
    });
    url = url.replace('?' + urlQueryParam, '');
  }

  queryParamSatrtIndex = url.indexOf(';');
  if (queryParamSatrtIndex !== -1) {
    const urlQueryParam = url.slice(queryParamSatrtIndex + 1, url.length);
    urlQueryParam.split('&').forEach(qps => {
      const a = qps.split('=');
      queryParam[a[0]] = a[1];
    });
  }

  return queryParam;
}

export function isIOS() {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
  // iPad on iOS 13 detection
  || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
}


/** 解析 JWT token */
export function parseJWTtoken(accessToken: string) {
  const base64Url = accessToken.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  switch (base64.length % 4) {
    case 0: break;
    case 2: base64 += '=='; break;
    case 3: base64 += '='; break;
    default: break;
  }

  const jwtPayloadStr = decodeURIComponent(escape(atob(base64)));
  return JSON.parse(jwtPayloadStr);
}

/** 檢查是否為觸控螢幕裝置 */
export function checkIsTouchMobile(): boolean {
  let hasTouchScreen = false;
  const navigator: Navigator = window.navigator;
  if ('maxTouchPoints' in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0;
  } else if ('msMaxTouchPoints' in navigator) {
    hasTouchScreen = (navigator as Navigator)['msMaxTouchPoints'] > 0;
  } else {
    const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
    if (mQ && mQ.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches;
    } else if ('orientation' in window) {
      hasTouchScreen = true; // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      const UA = (navigator as Navigator).userAgent;
      hasTouchScreen = (
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
      );
    }
  }
  return hasTouchScreen;
}

/** 在 server side 和 client side 做路徑轉導 */
export function navigateCrossPlatform(isBrowser: boolean, router: Router, response: Response, path: string[]): Promise<any>|Response {
  if (isBrowser) {
    return router.navigate(path);
  } else {
    response.redirect(302, path.join('/'));
    response.finished = true;
    response.end();
    return response;
  }
}

/** 分享到FB */
export function share2FB(fbAppId: string, sharePageUrl: string) {
  const isMobile = checkIsMobile();
  if (isMobile) {
    if (location.href.startsWith('https://') && fbAppId) {
      window.open(
        `https://www.facebook.com/dialog/share?app_id=${fbAppId}&display=popup&href=${encodeURIComponent(sharePageUrl)}&redirect_uri=${encodeURIComponent(sharePageUrl)}`,
        '_blank'
      );
    } else {
      window.open(
        'https://www.facebook.com/sharer.php?u=' + encodeURIComponent(sharePageUrl),
        '_blank'
      );
    }
  } else {
    // 桌面版分享
    window.open(
      'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(sharePageUrl),
      '_blank',
      'height=500,resizable=yes,scrollbars=yes,width=700'
    );
  }
}